import { Statuses, Time } from '@geee-be/core';
import type { Person } from '@local/common-lib';
import * as Sentry from '@sentry/react';
import type { FC, PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { setAppearanceSettingsFromProfile } from '../appearance-settings.js';
import { TokenProvider } from './token-provider.js';
import type { Auth, AuthenticatedState, AuthenticationState } from './types.js';
import { AuthStatus } from './types.js';

interface TrackingWindow extends Window {
  clarity?: (...args: unknown[]) => void;
  hj?: (...args: unknown[]) => void;
}

export const authContext = createContext<Auth>({
  refresh: async () => Promise.reject(new Error('Not configured')),
  signIn: async () => Promise.reject(new Error('Not configured')),
  signOut: async () => Promise.reject(new Error('Not configured')),
  state: undefined,
  switchOrganization: async () => Promise.reject(new Error('Not configured')),
});

// Provider component that wraps your app and makes auth object ...
// ... available to any child component that calls useAuth().
export const ProvideAuth: FC<PropsWithChildren> = ({ children }) => {
  const auth = useProvideAuth();

  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
};

// Hook for child components to get the auth object ...
// ... and re-render when it changes.
export const useAuth = (): Auth => {
  return useContext(authContext);
};

// Hook for child components to get the auth object throwing exception if not authenticated ...
// ... and re-render when it changes.
export const useAuthenticated = (): AuthenticatedState => {
  const context = useContext(authContext);
  if (context.state?.status !== AuthStatus.AUTHENTICATED) {
    throw new Error('Not authenticated');
  }
  return context.state;
};

// Provider hook that creates auth object and handles state
// https://usehooks.com/useAuth/
const useProvideAuth = (): Auth => {
  const [auth, setAuth] = useState<AuthenticationState | undefined>();
  const [tokenProvider, setTokenProvider] = useState<TokenProvider>();

  useEffect(() => {
    const provider = new TokenProvider({
      logger: {
        ...console,
        verbose: (...args: unknown[]) => console.debug(...args),
      },
      setAuth: (authenticationState) => {
        setAuth(authenticationState);

        if (authenticationState?.status === AuthStatus.AUTHENTICATED) {
          console.log('Trying to set tracking user information');
          authenticationState.axios
            .get<Person.View | Person.AccountView>('/api/v1/my/profile')
            .then(({ data, status }) => {
              if (status !== Number(Statuses.OK)) return;
              setAppearanceSettingsFromProfile(data.appearance);
              // a super admin has no organization, and so no roles
              const roles = 'roles' in data ? [...data.roles].sort() : [];
              const organizationId =
                'organizationId' in data ? data.organizationId : undefined;
              const common = {
                email: data.email,
                isAdmin: roles.includes('admin'),
                isPatroller: roles.includes('patroller'),
                isSummaryRecipient: roles.includes('summary-recipient'),
                isWatcher: roles.includes('watcher'),
                name: `${data.firstName} ${data.lastName.toLocaleUpperCase()}`.trim(),
                organizationId,
                roles: roles.join('+'),
              };
              Sentry.setContext('person', {
                id: data._id,
                ...common,
              });
              Sentry.setUser({
                email: data.email,
                id: data._id,
                name: `${data.firstName} ${data.lastName.toLocaleUpperCase()}`.trim(),
                segment: roles.join('+'),
              });

              const trackingWindow = window as TrackingWindow;
              trackingWindow.hj?.('identify', data._id, common);
              trackingWindow.clarity?.('identify', data._id);
              return undefined;
            })
            .catch((err) => {
              Sentry.captureException(err);
            });
        } else {
          Sentry.setContext('person', {});
        }
      },
      thresholdBeforeExpire: Time.seconds(5),
    });
    setTokenProvider(provider);

    return () => {
      provider.cleanup();
      setTokenProvider(undefined);
    };
  }, []);

  const refresh = (): Promise<boolean> => {
    return tokenProvider?.refresh() ?? Promise.resolve(false);
  };

  const signIn = async (
    email: string,
    password: string,
    remember: boolean,
    sharedDevice: boolean,
  ): Promise<boolean> => {
    return (
      tokenProvider?.signIn(email, password, remember, sharedDevice) ??
      Promise.resolve(false)
    );
  };

  const signOut = async (): Promise<void> => {
    await tokenProvider?.signOut();
  };

  const switchOrganization = async (organizationId: string): Promise<void> => {
    await tokenProvider?.switchOrganization(organizationId);
  };

  // Return the user object and auth methods
  return {
    refresh,
    signIn,
    signOut,
    state: auth,
    switchOrganization,
  };
};
